import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { X, Plus, Trash2 } from 'lucide-react';

import { useMyExpenseApi } from './config/useMyExpenseApi';
import { useExpenseEntryPolicy } from './config/useExpenseEntryPolicy';
import { formatPolicyAmount } from './config/expensePolicyHelpers';
import ExpensePolicyNotice from './components/ExpensePolicyNotice';
import { isReceiptComplete } from './config/receiptValidation';
import { parseTableParams, formatDate } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import PictureUpload from '../../components/ui/PictureUpload';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { getExpenseProjectOptions } from '../../services/expenseProjectService';

const Container = styled.div``;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding-top: 40px;
  overflow-y: auto;
`;

const DialogBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 620px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
  margin-bottom: 40px;
`;

const DialogTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
`;

const CategoryLimitHint = styled.div`
  margin-top: 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary || '#5A6B7B'};
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1.5px solid #E0E4E8;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ToggleBtn = styled.button`
  flex: 1;
  height: 48px;
  border: none;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active, theme }) => $active ? theme.colors.primary.main : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#004242'};

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primary.dark : 'rgba(2, 145, 239, 0.06)'};
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const ReceiptsSection = styled.div`
  margin-top: 8px;
`;

const ReceiptsSectionTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ReceiptCard = styled.div`
  border: 1.5px solid #E0E4E8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  position: relative;
`;

const ReceiptHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ReceiptName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ReceiptInfo = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #6b7280;
`;

const RemoveReceiptBtn = styled.button`
  background: none;
  border: none;
  color: #d32f2f;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  &:hover { color: #b71c1c; }
`;

const ReceiptFormCard = styled.div`
  border: 1.5px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  background: rgba(2, 145, 239, 0.02);
`;

const ReceiptFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 12px 0;
  border-top: 1px solid #E0E4E8;
  margin-top: 8px;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const emptyReceipt = () => ({
  date: null,
  amount: '',
  taxRate: '0',
  merchantName: '',
  mediaUrl: null,
  _file: null,
});

export default function MyExpensesPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, id: null });
  const [canceling, setCanceling] = useState(false);

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expenseType, setExpenseType] = useState('expense');
  const [categoryId, setCategoryId] = useState(null);
  // İsteğe bağlı proje — yalnız aktif projeler listelenir; kiracıda hiç aktif proje yoksa
  // seçici hiç gösterilmez.
  const [projectId, setProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [currency, setCurrency] = useState('TRY');
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [showReceiptForm, setShowReceiptForm] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState(emptyReceipt());
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  // Limit aşımı bloklamaz, ama kazara gönderim de olmasın: kasıtlı onay istenir.
  const [policyConfirmOpen, setPolicyConfirmOpen] = useState(false);

  const { columns: baseColumns, data, fetching, pagination, create, cancel } = useMyExpenseApi(queryParams);

  // Fetch categories
  useEffect(() => {
    api.get('/v1/expense-categories?pageSize=500')
      .then((res) => setCategories(res.data?.data?.data || []))
      .catch(() => {});
  }, []);

  // Aktif projeler (seçici). Liste her diyalog açılışında tazelenir — yeni açılan proje
  // sayfa yenilenmeden görünsün.
  useEffect(() => {
    if (!dialogOpen) return;
    getExpenseProjectOptions()
      .then((res) => {
        const items = Array.isArray(res.data?.data) ? res.data.data : [];
        setProjects(items.map((p) => ({ id: p.id, name: p.code ? `${p.name} (${p.code})` : p.name })));
      })
      .catch(() => {});
  }, [dialogOpen]);

  const filteredCategories = useMemo(() => {
    return categories.filter((c) => c.type === expenseType);
  }, [categories, expenseType]);

  const handleNewButtonClick = useCallback(() => {
    setExpenseType('expense');
    setCategoryId(null);
    setProjectId(null);
    setCurrency('TRY');
    setDescription('');
    setTotalAmount('');
    setReceipts([]);
    setShowReceiptForm(false);
    setCurrentReceipt(emptyReceipt());
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback((row) => {
    setCancelConfirm({ isOpen: true, id: row.id });
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    setCanceling(true);
    await cancel(cancelConfirm.id);
    setCancelConfirm({ isOpen: false, id: null });
    setCanceling(false);
  }, [cancel, cancelConfirm.id]);

  const handleRowClick = useCallback((row) => {
    if (row) history.push(`/expenses/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Receipt management
  const handleAddReceipt = useCallback(() => {
    setShowReceiptForm(true);
    setCurrentReceipt(emptyReceipt());
  }, []);

  const isReceiptValid = useMemo(() => isReceiptComplete(currentReceipt), [currentReceipt]);

  const handleSaveReceipt = useCallback(() => {
    if (!isReceiptValid) return;
    setReceipts((prev) => [...prev, { ...currentReceipt }]);
    setShowReceiptForm(false);
    setCurrentReceipt(emptyReceipt());
  }, [currentReceipt, isReceiptValid]);

  const handleRemoveReceipt = useCallback((index) => {
    setReceipts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const receiptsTotal = useMemo(() => {
    return receipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  }, [receipts]);

  const { entryPolicy, categoryHint } = useExpenseEntryPolicy({
    expenseType, categoryId, currency, totalAmount, receiptsTotal, categories,
  });

  const uploadFile = useCallback(async (file) => {
    if (!file) return null;
    try {
      // Backend /v1/storage/presign reads fileName + mimeType from the query
      // string and returns snake_case fields WITHOUT the ResponseObjectModel
      // envelope ({ upload_url, file_url, filename }) — read presignRes.data,
      // not presignRes.data.data. The envelope read left upload_url empty, so
      // the receipt was never uploaded and every web expense was rejected with
      // expense.receiptMediaRequired.
      const presignRes = await api.post(
        `/v1/storage/presign?fileName=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.type)}&purpose=${encodeURIComponent('expenseReceipt')}`,
      );
      const { upload_url, file_url } = presignRes.data || {};
      if (upload_url) {
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        // A failed PUT must not hand back a file_url that points at nothing.
        if (!putRes.ok) return null;
        return file_url;
      }
    } catch {
      return null;
    }
    return null;
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // Upload receipt files
      const processedReceipts = [];
      for (const receipt of receipts) {
        let mediaUrl = receipt.mediaUrl;
        if (receipt._file instanceof File) {
          mediaUrl = await uploadFile(receipt._file);
        }
        processedReceipts.push({
          date: receipt.date,
          amount: parseFloat(receipt.amount) || 0,
          taxRate: parseFloat(receipt.taxRate) || 0,
          merchantName: receipt.merchantName?.trim() || null,
          mediaUrl,
        });
      }

      const payload = {
        type: expenseType,
        categoryId,
        projectId: projectId || null,
        currency,
        description,
        totalAmount: expenseType === 'advance' ? parseFloat(totalAmount) || 0 : receiptsTotal,
        receipts: expenseType === 'expense' ? processedReceipts : [],
      };

      const success = await create(payload);
      if (success) setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  }, [expenseType, categoryId, projectId, currency, description, totalAmount, receipts, receiptsTotal, create, uploadFile]);

  const handleSaveClick = useCallback(() => {
    if (entryPolicy) {
      setPolicyConfirmOpen(true);
      return;
    }
    handleSave();
  }, [entryPolicy, handleSave]);

  const handlePolicyConfirm = useCallback(async () => {
    setPolicyConfirmOpen(false);
    await handleSave();
  }, [handleSave]);

  const actionOptions = useMemo(() => [
    { type: 'details', label: t('expense.detail'), onClick: (row) => history.push(`/expenses/${row.id}`) },
    {
      type: 'delete', label: t('expense.cancel'), onClick: handleCancel,
      condition: (row) => row.canBeCanceled === true,
    },
  ], [t, handleCancel, history]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const currencyOptions = useMemo(() => [
    { id: 'TRY', name: 'TRY' },
    { id: 'USD', name: 'USD' },
    { id: 'EUR', name: 'EUR' },
    { id: 'GBP', name: 'GBP' },
  ], []);

  return (
    <Container>
      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('expense.cancelTitle')}
        message={t('expense.cancelMessage')}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, id: null })}
        confirmText={t('expense.cancel')}
        loading={canceling}
      />

      <ConfirmDialog
        isOpen={policyConfirmOpen}
        title={t('expense.policy.confirmTitle')}
        message={entryPolicy
          ? t('expense.policy.confirmBody', { amount: formatPolicyAmount(entryPolicy.exceededAmount, currency) })
          : ''}
        onConfirm={handlePolicyConfirm}
        onCancel={() => setPolicyConfirmOpen(false)}
        confirmText={t('expense.policy.confirmSubmit')}
        loading={saving}
      />

      {dialogOpen && (
        <Overlay onClick={() => setDialogOpen(false)}>
          <DialogBox onClick={(e) => e.stopPropagation()}>
            <DialogTitle>{t('expense.new')}</DialogTitle>

            <ToggleGroup>
              <ToggleBtn $active={expenseType === 'expense'} onClick={() => { setExpenseType('expense'); setCategoryId(null); }}>
                {t('expense.expense')}
              </ToggleBtn>
              <ToggleBtn $active={expenseType === 'advance'} onClick={() => { setExpenseType('advance'); setCategoryId(null); }}>
                {t('expense.advance')}
              </ToggleBtn>
            </ToggleGroup>

            <FormGrid>
              <GenericSelect
                label={t('expense.category')}
                options={filteredCategories}
                value={categoryId}
                onChange={setCategoryId}
                idKey="id"
                labelKey="name"
                required
              />

              {projects.length > 0 && (
                <GenericSelect
                  label={t('expense.project')}
                  options={projects}
                  value={projectId}
                  onChange={(v) => setProjectId(v || null)}
                  idKey="id"
                  labelKey="name"
                  placeholder={t('expense.selectProject')}
                />
              )}

              <GenericSelect
                label={t('expense.currency')}
                options={currencyOptions}
                value={currency}
                onChange={setCurrency}
                idKey="id"
                labelKey="name"
              />

              <TextInput
                label={t('expense.description')}
                type="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />

              {expenseType === 'advance' && (
                <TextInput
                  label={t('expense.totalAmount')}
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  required
                />
              )}

              {expenseType === 'expense' && (
                <ReceiptsSection>
                  <ReceiptsSectionTitle>
                    <span>{t('expense.receipts')}</span>
                    <Button variant="ghost" onClick={handleAddReceipt} style={{ padding: '6px 12px', fontSize: 13 }}>
                      <Plus size={16} /> {t('expense.addReceipt')}
                    </Button>
                  </ReceiptsSectionTitle>

                  {receipts.map((receipt, idx) => (
                    <ReceiptCard key={idx}>
                      <ReceiptHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <ReceiptInfo>{receipt.amount} {currency}</ReceiptInfo>
                          <ReceiptInfo>{receipt.date ? formatDate(receipt.date, i18n.language) : '-'}</ReceiptInfo>
                          <RemoveReceiptBtn onClick={() => handleRemoveReceipt(idx)} title={t('expense.removeReceipt')}>
                            <Trash2 size={16} />
                          </RemoveReceiptBtn>
                        </div>
                      </ReceiptHeader>
                    </ReceiptCard>
                  ))}

                  {showReceiptForm && (
                    <ReceiptFormCard>
                      <ReceiptFormGrid>
                        <DateTimePicker
                          label={t('expense.receiptDate')}
                          value={currentReceipt.date}
                          onChange={(val) => setCurrentReceipt((p) => ({ ...p, date: val }))}
                          mode="date"
                          maxDate={`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`}
                        />
                        <TextInput
                          label={t('expense.receiptAmount')}
                          type="number"
                          value={currentReceipt.amount}
                          onChange={(e) => setCurrentReceipt((p) => ({ ...p, amount: e.target.value }))}
                          required
                          min="0"
                        />
                        <TextInput
                          label={t('expense.receiptTaxRate')}
                          type="number"
                          value={currentReceipt.taxRate}
                          onChange={(e) => setCurrentReceipt((p) => ({ ...p, taxRate: e.target.value }))}
                          min="0"
                        />
                        <TextInput
                          label={t('expense.merchantName')}
                          value={currentReceipt.merchantName}
                          onChange={(e) => setCurrentReceipt((p) => ({ ...p, merchantName: e.target.value }))}
                          maxLength={200}
                        />
                        <FullWidthField>
                          <PictureUpload
                            label={t('expense.receiptMedia')}
                            required
                            value={currentReceipt._file}
                            onChange={(file) => setCurrentReceipt((p) => ({ ...p, _file: file }))}
                            accept="image/*,.pdf"
                            validTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']}
                            maxSize={10}
                          />
                        </FullWidthField>
                        <FullWidthField style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <Button variant="outline" onClick={() => setShowReceiptForm(false)} style={{ padding: '8px 16px' }}>
                            <X size={16} />
                          </Button>
                          <Button onClick={handleSaveReceipt} disabled={!isReceiptValid} style={{ padding: '8px 16px' }}>
                            <Plus size={16} /> {t('expense.addReceipt')}
                          </Button>
                        </FullWidthField>
                      </ReceiptFormGrid>
                    </ReceiptFormCard>
                  )}

                  {receipts.length > 0 && (
                    <TotalRow>
                      {t('expense.total')}: {receiptsTotal} {currency}
                    </TotalRow>
                  )}

                  {receipts.length === 0 && !showReceiptForm && (
                    <div style={{ textAlign: 'center', color: '#A0A8B4', padding: '20px 0', fontFamily: "'Nunito Sans', sans-serif", fontSize: 13 }}>
                      {t('expense.noReceipts')}
                    </div>
                  )}
                </ReceiptsSection>
              )}
            </FormGrid>

            {entryPolicy && (
              <ExpensePolicyNotice policy={entryPolicy} variant="entry" style={{ marginTop: 16 }} />
            )}

            {!entryPolicy && categoryHint != null && (
              <CategoryLimitHint>
                {t('expense.policy.categoryLimitHint', { limit: formatPolicyAmount(categoryHint, currency) })}
              </CategoryLimitHint>
            )}

            <DialogActions>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveClick} disabled={saving}>
                {saving ? '...' : t('common.save')}
              </Button>
            </DialogActions>
          </DialogBox>
        </Overlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('expense.myExpenses')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={true}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination,
        handleNewButtonClick, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
